import React from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
} from "recharts";
import { Filter, AlertTriangle, HelpCircle } from "lucide-react";

/**
 * Pipeline operator views — the 5-stage funnel and the run-history breakdown.
 *
 * These answer "why did the count move?", which is an operator question, not an
 * investor one — they live in the Admin console, not on the Dashboard. The i18n
 * keys stay under `dashboard.*` because the strings themselves are unchanged.
 */

export function PipelineFunnel({ funnel, t }) {
  if (!funnel?.available) return null;
  return (
    <section className="border border-border p-6" data-testid="pipeline-funnel">
      <div className="overline">{t("dashboard.pipelineFunnel")}</div>
      <h3 className="mt-1 font-heading text-2xl font-bold flex items-center gap-2">
        <Filter size={20} /> {t("dashboard.fiveStageFilter")}
      </h3>
      <div className="mt-5 space-y-2">
        {funnel.funnel.map((row, i) => {
          const max = funnel.funnel[0].count || 1;
          const w = (row.count / max) * 100;
          const colorCls = row.color === "green" ? "bg-signal-green" : row.color === "yellow" ? "bg-signal-yellow" : "bg-zinc-700";
          const txtCls = row.color === "green" ? "text-signal-green" : row.color === "yellow" ? "text-signal-yellow" : "text-text-secondary";
          return (
            <div key={row.stage}>
              <div className="flex justify-between text-[11px] font-mono">
                <span className="text-text-muted">{String(i + 1).padStart(2, "0")} · {row.stage}</span>
                <span className={`${txtCls} num`}>{row.count.toLocaleString()}</span>
              </div>
              <div className="mt-1 h-2 bg-zinc-900 border border-border">
                <div className={`h-full ${colorCls}`} style={{ width: `${w}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-5 text-[10px] font-mono text-text-muted">
        {t("dashboard.funnelFooter")}
      </div>
    </section>
  );
}

export function RunHistory({ runs, t }) {
  if (!runs.length) {
    return (
      <div className="mt-5 py-10 text-center border border-dashed border-border">
        <HelpCircle size={22} className="mx-auto text-text-muted" />
        <p className="mt-2 text-xs text-text-muted">{t("dashboard.noRunsLogged")}</p>
      </div>
    );
  }

  const chrono = [...runs].reverse().map((r) => ({
    ...r,
    label: r.ran_at ? new Date(r.ran_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "?",
  }));
  const latest = runs[0];
  const diag = latest?.diag;
  const sourceLabel = { local: t("dashboard.sourceLocal"), live: t("dashboard.sourceLive"), unavailable: t("dashboard.sourceUnavailable") }[latest?.source] || (latest?.source || "?").toUpperCase();

  return (
    <div className="mt-5 grid grid-cols-12 gap-6">
      {/* Sparkline of listing_count across recent runs */}
      <div className="col-span-12 lg:col-span-7">
        <div className="text-[10px] font-mono uppercase tracking-widest2 text-text-muted mb-2">
          {t("dashboard.listingCountLastRuns", { count: chrono.length, unit: chrono.length === 1 ? t("dashboard.run") : t("dashboard.runs") })}
        </div>
        <div className="h-32">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chrono} margin={{ left: -20, right: 10, top: 5 }}>
              <XAxis dataKey="label" stroke="#71717A" fontSize={9} tickLine={false} axisLine={{ stroke: "#27272A" }} interval="preserveStartEnd" />
              <YAxis stroke="#71717A" fontSize={9} tickLine={false} axisLine={{ stroke: "#27272A" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#09090B", border: "1px solid #27272A", fontSize: 11, fontFamily: "JetBrains Mono" }}
                labelStyle={{ color: "#A1A1AA" }}
                formatter={(v, k) => [v, k === "listing_count" ? t("common.listings") : k]}
              />
              <Line type="monotone" dataKey="listing_count" stroke="#00FF66" strokeWidth={1.5} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Latest run's diagnostic breakdown */}
      <div className="col-span-12 lg:col-span-5 border border-border p-4">
        <div className="flex items-center justify-between">
          <span className="overline">{t("dashboard.latestRun", { source: sourceLabel })}</span>
          <span className="text-[10px] font-mono text-text-muted">{latest?.ran_at ? new Date(latest.ran_at).toLocaleString("en-GB") : "—"}</span>
        </div>
        {!diag ? (
          <div className="mt-3 space-y-2 text-[11px] font-mono">
            <div className="flex justify-between">
              <span className="text-text-muted">{t("common.listings")}</span>
              <span className="num text-white">{latest?.listing_count ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">{t("common.markets")}</span>
              <span className="num text-white">{latest?.market_count ?? "—"}</span>
            </div>
          </div>
        ) : (
          <div className="mt-3 space-y-2 text-[11px] font-mono">
            <div className="flex justify-between">
              <span className="text-text-muted">{t("dashboard.scrapedRaw")}</span>
              <span className="num text-white">{diag.scraped_total ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">{t("dashboard.dedupRemoved")}</span>
              <span className="num text-signal-yellow">{diag.dedup_removed ?? 0}</span>
            </div>
            <div className="flex justify-between border-t border-border pt-2">
              <span className="text-text-muted">{t("dashboard.pushedThisRun")}</span>
              <span className="num text-white">{latest.listing_count}</span>
            </div>
            {diag.enrich_candidates != null && (
              <div className="flex justify-between">
                <span className="text-text-muted">{t("dashboard.enrichBreakdown")}</span>
                <span className="num text-text-secondary">
                  {diag.enrich_candidates} / {diag.enrich_done} / {diag.enrich_capped}
                </span>
              </div>
            )}
            {(diag.market_failures?.length > 0 || diag.portal_failures?.length > 0) && (
              <div className="flex items-start justify-between gap-2 border-t border-border pt-2">
                <span className="text-signal-red flex items-center gap-1">
                  <AlertTriangle size={11} /> {t("dashboard.scrapeFailures")}
                </span>
                <span className="num text-signal-red text-right">
                  {(diag.market_failures?.length || 0) + (diag.portal_failures?.length || 0)}
                  <span className="block text-[9px] text-text-muted font-normal normal-case">
                    {t("dashboard.scrapeFailuresNote")}
                  </span>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
